import React, { useEffect, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCIcon, RCCTooltip, arrayMove } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import ModalFields from "src/pages/Indicators/Fields/modals";
import Title from "src/components/Title";
import { useWatch } from "react-hook-form";
import { v4 } from "uuid";
import returnStringValueForSelect from "src/utils/returnStringValueForSelect";
import { IRefIndicatorFieldModals } from "src/pages/Indicators/Fields/interface";
import SortableContainer from "src/components/Sortable/Container";
import areaChart from "src/assets/icons/area-chart.svg";
import chart from "src/assets/icons/chart.svg";
import lineChart from "src/assets/icons/line-chart.svg";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { baseIqtbc002, GraphicType, IFieldsTab, IIqtbc002 } from "../interface";
import TabFieldsItem from "./tabFieldsItem";

const getSrcChartType = (chartType: string) => {
	if(GraphicType.AREA === chartType.toString())
		return areaChart;
	if(GraphicType.LINE === chartType.toString())
		return lineChart;
	return chart;
};

const TabFields: React.FC<IFieldsTab> = ({ 
	indicatorsFields,
	simpleField,
	selectFieldtoGoal,
	register,
	setValue,
	control,
	errors,
	clearErrors,
	selectedIndicator
}) => {

	const {translate} = useTranslation();
	const [selectedFieldtoGoal,setSelectedFieldtoGoal] = useState(selectFieldtoGoal);
	const [lovFields,setLovFields] = useState<Array<{value:string,label:string}>>([]);
	const refModals = useRef<IRefIndicatorFieldModals>(null);

	const fieldsWatch = useWatch({
		control,
		name: "fields"
	});

	const selectedGoalFieldWatch = useWatch({
		control,
		name: "selectedGoalField",
		defaultValue:selectFieldtoGoal
	});

	const controlledFields = indicatorsFields.map((field, index) => ({
		...field,
		...fieldsWatch && fieldsWatch[index]
 	}));

	useEffect(() => {
		if(fieldsWatch){
			setLovFields(() => {
				const  temporaryFields = [] as Array<{value:string,label:string}>;
				fieldsWatch.forEach((field:IIqtbc002) => 
				{
					if(field.iqc002Iqb001Id){
						temporaryFields.push({value: returnStringValueForSelect(field.iqc002Iqb001Id),label:field.description||""});
					} 
				});
				return temporaryFields;
			});
		}
	}, [fieldsWatch]);
	
	useEffect(() => {
		setSelectedFieldtoGoal(selectedGoalFieldWatch);
	}, [selectedGoalFieldWatch]);

	const handleDragEnd = (activeId: string, overId: string) => {
		const activeIndex = controlledFields.findIndex((field) => field.id === activeId);
		const overIndex = controlledFields.findIndex((field) => field.id === overId);

		let temp = [...controlledFields];
		temp= arrayMove(temp, 
			activeIndex, 
			overIndex
		);
	
		setValue("fields",temp);
	};

	const addField = (iqb001Id?: string) => {
		if(errors.fields?.message)
			clearErrors("fields");
		const newId = v4();
		const newObj ={
			...baseIqtbc002,
			iqc002Iqb001Id: iqb001Id || "0",
			iqc002Tpgrafacum:GraphicType.COLUMN,
			iqc002Tipografico:GraphicType.COLUMN,
			id:newId
		} as IIqtbc002;

		if(!selectedFieldtoGoal){
			setValue("selectedGoalField",newId);
		}

		const prevState = [...controlledFields];
		setValue("fields",[...prevState,newObj]);
	};

	const updateField = (label: string, index: number) => {
		if(errors.fields?.message)
			clearErrors("fields");
		setValue(`fields.${index}.description`, label);
	}; 

	const removeField = (id:string) => {
		const prevState = [...controlledFields];
		const newFields = prevState.filter((x) => x.id !== id);
		setValue("fields", newFields);
	};

	const fieldElements = controlledFields.map((field:IIqtbc002,index) => (
		<TabFieldsItem 
			key={field.id}
			index={index}
			field={field}
			lovFields={lovFields}
			selectedIndicator={selectedIndicator}
			selectedFieldtoGoal={selectedFieldtoGoal}
			simpleField={simpleField}
			control={control}
			register={register}
			errors={errors}
			setValue={setValue}
			fieldsWatch={fieldsWatch}
			updateField={updateField}
			removeField={removeField}
			getSrcChartType={getSrcChartType}
			refModals={refModals}	
		/>
	));

	const onCallbackAddFields = (id: number) => {
		addField(String(id));
	};

	return(
		<RCCGrid container item 
			justifyContent="center"
		>
			<RCCGrid container item 
				xs={12} 
				sm={10} 
				lg={8}
			>
				<Title 
					size="xs"
					color="grey"
				>
					{translate("Os campos nos indicadores devem ser utilizados para inserção dos valores relacionados ao indicador")}. Ex: {translate("Valor de Faturamento no indicador de Faturamento mensal")}.
				</Title>
				<RCCGrid container 
					justifyContent="space-between" 
					alignContent="flex-end" 
					wrap="nowrap"
				>
					{errors.fields &&
						<RCCGrid container item 
							alignItems="center"
						>	
							<RCCIcon 
								name="errorOutline" 
								size={19} 
								color="var(--red)"
							/>
							<Title 
								color="red" 
								size="sm"
							>
								{errors.fields?.message}
							</Title>
						</RCCGrid>
					}
					<RCCGrid container item
						justifyContent="flex-end"
					>
						<RCCTooltip
							text={translate("Adicionar campo")}
							color="purple">
							<RCCButton 
								onClick={() => {addField();}} 
								size="sm" 
								color="purple" 
								icon={{name:"plus"}} 
								layout="isRounded"/>
						</RCCTooltip>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE} path="/indicators/fields">
							<RCCTooltip
								text={translate("Adicionar campo avançado")}
								color="grey">
								<RCCButton 
									onClick={() => { refModals.current?.actionButtonAdd(onCallbackAddFields); }}
									size="sm" 
									color="grey"
									icon={{name:"plus"}} 
									layout="isRounded"
								/>
							</RCCTooltip>
						</CheckPermission>
					</RCCGrid>
				</RCCGrid>
				<SortableContainer 
					items={controlledFields}
					onDragEnd={handleDragEnd}
					modifiers={[restrictToVerticalAxis]}
				>	
					{fieldElements}
				</SortableContainer>
				
			</RCCGrid>
			<ModalFields ref={refModals} />
		</RCCGrid>
	);
};
export default TabFields;
